import { z } from 'zod';
import { ufSchema } from './cidades.ts';
import {
  booleanoDeQuerySchema,
  dataHoraSchema,
  paginacaoSchema,
  regimeTributarioOpcionalSchema,
  regimeTributarioSchema,
  respostaListaSchema,
  semRegimeDePessoaFisica,
  tipoPessoaOuNaoInformadoSchema,
  uuidSchema,
} from './comum.ts';
import { contatoDoFornecedorRespostaSchema, contatoDoFornecedorSchema } from './contato.ts';
import { ehDocumentoValido } from './documento.ts';
import { enderecoSchema, textoOpcional } from './endereco.ts';

/**
 * Fornecedor é uma linha de `pessoas` com `is_fornecedor = true` (.spec/016).
 *
 * Mesmo cadastro de empresa e cliente, mesma tabela, outra flag. As diferenças
 * em relação ao contrato de empresa são três, e as três são de negócio:
 *
 * - **Documento é opcional.** Na empresa ele decide o crédito de imposto e a
 *   apuração não roda sem ele; no fornecedor, o cadastro costuma nascer do
 *   orçamento, antes de o CNPJ estar à mão. Exigi-lo empurraria o usuário a
 *   inventar número, e número inventado não se distingue do verdadeiro.
 * - **Tipo e regime podem ficar em aberto.** Quem não tem o documento em mãos
 *   com frequência também não sabe se aquilo é CPF ou CNPJ, nem sob que regime
 *   o fornecedor apura — órgão público é o caso típico. Forçar a escolha
 *   gravaria o palpite como fato. O regime do fornecedor não decide crédito
 *   nenhum: quem credita é a empresa que compra (`.spec/001`, seção 2.2).
 * - **`isEmpresa` viaja no payload.** É o que permite promover um fornecedor a
 *   empresa do grupo sem abrir um segundo cadastro com o mesmo CNPJ — e é o
 *   campo que a API só aceita de quem tem permissão sobre empresa.
 */

/* -------------------------------------------------------------------------- */
/* Compras — o que o fornecedor oferece                                       */
/* -------------------------------------------------------------------------- */

/**
 * O que o fornecedor vende, declarado em dois níveis.
 *
 * **Grupos ofertados** é a regra geral: marcar "Elétrica" diz que o fornecedor
 * atende o ramo inteiro abaixo dele — "Elétrica › Cabos", "Elétrica › Cabos ›
 * Flexível" e o que vier depois. É o nível em que o comprador raciocina, e é o
 * que evita ter de remarcar o fornecedor a cada subgrupo novo criado no
 * cadastro.
 *
 * **Insumos não ofertados** é a exceção nominal: o distribuidor de elétrica que
 * não trabalha com eletroduto. Sem a exceção, a única saída seria desmarcar o
 * grupo e marcar um a um os subgrupos restantes — o que reintroduz exatamente o
 * trabalho que a herança de ramo eliminou.
 *
 * A exceção **não é recortada pelos grupos marcados**: qualquer insumo pode
 * entrar. A alternativa — recusar o insumo fora do ramo — foi descartada porque
 * o grupo do insumo muda no cadastro dele, e uma reclassificação silenciosa
 * apagaria a exceção que alguém registrou de propósito. Exceção sobrando é
 * inerte; exceção apagada é uma compra indevida.
 *
 * Nenhuma das duas listas é obrigatória. Fornecedor sem grupo nenhum é o caso
 * comum do cadastro nascido às pressas, do orçamento: exigir a classificação
 * ali empurraria o usuário a marcar qualquer coisa para conseguir salvar.
 */

/** Vínculo com grupo, como ele volta da leitura. */
export const fornecedorGrupoInsumoSchema = z.object({
  grupoInsumoId: uuidSchema,
  /** Desnormalizado na leitura, para a tela não pedir a árvore só pelos nomes. */
  grupoNome: z.string(),
});

export type FornecedorGrupoInsumo = z.infer<typeof fornecedorGrupoInsumoSchema>;

/** Exceção nominal, como ela volta da leitura. */
export const fornecedorInsumoNaoOfertadoSchema = z.object({
  insumoId: uuidSchema,
  insumoNome: z.string(),
  /**
   * Grupo do insumo, desnormalizado.
   *
   * A exceção só se entende ao lado do grupo: "Eletroduto 3/4" na lista não diz
   * qual regra geral ele está furando; "Eletroduto 3/4 · Elétrica › Conduítes"
   * diz.
   */
  grupoNome: z.string(),
});

export type FornecedorInsumoNaoOfertado = z.infer<typeof fornecedorInsumoNaoOfertadoSchema>;

/**
 * As duas listas no payload, como **estado final** e não como delta.
 *
 * Ids soltos, sem objeto envolvente: ao contrário do vínculo insumo × empresa,
 * aqui a linha não carrega atributo nenhum — ela é a própria afirmação. Um
 * objeto de um campo só existiria para o dia em que houvesse um segundo, e nesse
 * dia o schema muda de qualquer forma.
 */
const camposDeCompras = {
  gruposInsumos: z.array(uuidSchema).prefault([]),
  insumosNaoOfertados: z.array(uuidSchema).prefault([]),
};

/**
 * Acusa ids repetidos, apontando a posição.
 *
 * Os índices únicos das duas tabelas recusariam de qualquer forma, com uma
 * violação de constraint que o usuário não consegue interpretar. Aqui o erro
 * chega preso à linha da lista.
 */
function acusarDuplicados(
  ctx: z.RefinementCtx,
  ids: readonly string[],
  campo: 'gruposInsumos' | 'insumosNaoOfertados',
  mensagem: string,
): void {
  const vistos = new Set<string>();

  for (const [indice, id] of ids.entries()) {
    if (vistos.has(id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [campo, indice], message: mensagem });
    }
    vistos.add(id);
  }
}

function validarCompras(
  dados: { gruposInsumos: string[]; insumosNaoOfertados: string[] },
  ctx: z.RefinementCtx,
): void {
  acusarDuplicados(
    ctx,
    dados.gruposInsumos,
    'gruposInsumos',
    'Este grupo já está marcado para o fornecedor.',
  );
  acusarDuplicados(
    ctx,
    dados.insumosNaoOfertados,
    'insumosNaoOfertados',
    'Este insumo já está na lista de exceções.',
  );
}

/**
 * Resumo das duas listas, em uma linha.
 *
 * Fica no contrato porque a tela e a API dizem a mesma coisa sobre o mesmo
 * cadastro; duas redações divergem na primeira correção, e a divergência aparece
 * como dois números diferentes para o mesmo fornecedor.
 *
 * Recebe **contagens**, e não as listas. Antes tomava dois arrays e chamava
 * `.length`, porque o único chamador era a tela do cadastro, que as tem na mão.
 * O chamador de hoje é a listagem, e ali as listas não existem — a API manda os
 * dois números, contados no banco (ver `qtdGruposInsumos`). Pedir arrays
 * obrigaria a listagem a inventar dois vetores do tamanho certo só para o
 * `.length` deles ser lido de volta.
 */
export function resumoDeCompras(compras: { grupos: number; excecoes: number }): string {
  const { grupos, excecoes } = compras;

  if (grupos === 0 && excecoes === 0) return 'Nada informado';

  const partes = [grupos === 1 ? '1 grupo' : `${grupos} grupos`];
  if (excecoes > 0) partes.push(excecoes === 1 ? '1 exceção' : `${excecoes} exceções`);

  return partes.join(' · ');
}

const camposFornecedor = {
  tipo: tipoPessoaOuNaoInformadoSchema,
  nome: z.string().trim().min(2, 'Informe o nome ou a razão social.').max(200),
  /** Em PF repete o conteúdo de `nome`; o service preenche quando vier vazio. */
  nomeFantasia: z.string().trim().max(200).optional(),
  /** Vazio é aceito e vira `null`; preenchido, o dígito verificador é conferido. */
  documento: z.string().trim().max(20).prefault(''),
  regimeTributario: regimeTributarioOpcionalSchema,
  /**
   * Promove o fornecedor a empresa do próprio tenant.
   *
   * Gravar `true` aqui **exige permissão sobre empresa** — a checagem é da
   * rota, não do schema: o contrato descreve formato, não autorização.
   */
  isEmpresa: z.boolean().prefault(false),
  isCliente: z.boolean().prefault(false),
  /**
   * Condição de pagamento com que o pedido a este fornecedor nasce.
   *
   * **Opcional, e vazia é o normal**: a maioria dos fornecedores negocia a
   * condição por pedido, e o comprador a escolhe na tela do pedido. Ela existe
   * para o outro caso — a compra recorrente com prazo fixo acordado —, e é o que
   * permite à geração de pedidos por **lista de compra direta** montar o
   * cabeçalho sem perguntar: o pedido gerado da lista não passa por tela nenhuma
   * antes de existir (`.spec/015`, decisão 46).
   *
   * A string vazia do `<select>` entra e sai como `null`: o formulário não guarda
   * `null` em campo de controle, e sem isso "não escolhi" chegaria aqui como `''`
   * e falharia na validação de uuid com uma mensagem sobre formato.
   *
   * **Não vale como padrão do pedido digitado à mão.** Sugeri-la ali seria
   * escolha da tela, não do contrato — e a tela do pedido já tem a condição como
   * campo obrigatório, que o comprador confere antes de gravar.
   */
  condicaoPagamentoPadraoId: uuidSchema
    .or(z.literal(''))
    .nullable()
    .transform((valor) => (valor === '' || valor === null ? null : valor))
    .prefault(null),
  observacao: textoOpcional(2000),
  ativo: z.boolean().prefault(true),
};

/**
 * Documento vazio passa; preenchido, precisa ser válido.
 *
 * Conferir só o tamanho deixaria `111.111.111-11` entrar no cadastro e virar
 * problema no dia da emissão da nota, quando já há movimento amarrado.
 *
 * Com o tipo `NI` não existe documento a conferir: não se sabe sequer qual dos
 * dois algoritmos aplicar. Recusar é melhor do que aceitar sem validar — o
 * mesmo `CHECK` existe no banco (`pessoas_nao_informado_sem_documento`).
 */
function validarDocumento(
  dados: { tipo: 'PF' | 'PJ' | 'NI'; documento: string },
  ctx: z.RefinementCtx,
): void {
  if (dados.tipo === 'NI') {
    if (dados.documento.length === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['documento'],
      message: 'Escolha Física ou Jurídica para informar o documento.',
    });
    return;
  }

  if (dados.documento.length === 0) return;
  if (ehDocumentoValido(dados.tipo, dados.documento)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['documento'],
    message: dados.tipo === 'PF' ? 'CPF inválido.' : 'CNPJ inválido.',
  });
}

/**
 * Empresa do grupo é **pessoa jurídica**, e com regime informado.
 *
 * Gêmea da do cliente, e a explicação inteira está lá — inclusive por que o
 * tipo passou a exigir `PJ` quando o regime sumiu da tela da pessoa física.
 */
function validarPapelEmpresa(
  dados: {
    tipo: 'PF' | 'PJ' | 'NI';
    regimeTributario: 'NORMAL' | 'SIMPLES' | 'MEI' | null;
    isEmpresa: boolean;
  },
  ctx: z.RefinementCtx,
): void {
  if (!dados.isEmpresa) return;

  if (dados.tipo !== 'PJ') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tipo'],
      message:
        'Empresa do grupo é pessoa jurídica: é ela que emite documento fiscal e tem regime tributário.',
    });
    return;
  }

  // É o regime da empresa que decide o direito a crédito de ICMS, PIS e COFINS
  // na entrada da nota. Sem ele, a apuração encontraria `NULL` no meio da
  // confirmação.
  if (dados.regimeTributario === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['regimeTributario'],
      message: 'Empresa do grupo precisa ter o regime tributário informado.',
    });
  }
}

/** A transformação vem depois das conferências — ver `criarClienteSchema`. */
export const criarFornecedorSchema = z
  .object({ ...camposFornecedor, ...camposDeCompras })
  .merge(enderecoSchema)
  // E-mail, telefone fixo, celular e nome do contato (decisão 115) — sem a
  // inscrição municipal do cliente, que este cadastro não mostra e por isso não
  // escreve. Ver `contatoDoFornecedorSchema`.
  .merge(contatoDoFornecedorSchema)
  .superRefine(validarDocumento)
  .superRefine(validarPapelEmpresa)
  .superRefine(validarCompras)
  .transform(semRegimeDePessoaFisica);

export type CriarFornecedor = z.infer<typeof criarFornecedorSchema>;

export const atualizarFornecedorSchema = criarFornecedorSchema;
export type AtualizarFornecedor = z.infer<typeof atualizarFornecedorSchema>;

/**
 * Resposta.
 *
 * `documento` volta apenas com dígitos; a máscara é aplicada na exibição por
 * `formatDocumento`, nunca gravada.
 */
export const fornecedorSchema = z
  .object({
    id: uuidSchema,
    tipo: tipoPessoaOuNaoInformadoSchema,
    nome: z.string(),
    nomeFantasia: z.string(),
    documento: z.string().nullable(),
    regimeTributario: regimeTributarioSchema.nullable(),
    isEmpresa: z.boolean(),
    isCliente: z.boolean(),
    /**
     * A condição de pagamento acordada, e o nome dela.
     *
     * O nome vem desnormalizado porque a listagem e a tela de geração de pedidos
     * o mostram — e resolvê-lo no cliente seria uma consulta por linha. Nulos no
     * fornecedor que negocia por pedido, que é a maioria.
     */
    condicaoPagamentoPadraoId: uuidSchema.nullable(),
    condicaoPagamentoPadraoNome: z.string().nullable(),
    observacao: z.string().nullable(),
    ativo: z.boolean(),

    /**
     * Quantos grupos o fornecedor atende, e quantas exceções ele declarou.
     *
     * **Contagem, e não as listas.** Ver `fornecedorDetalhadoSchema`, logo
     * abaixo, para por que as listas não vêm na listagem: em resumo, elas
     * custariam duas consultas por página e voltariam sempre vazias no caminho
     * da lista, o que a tela leria como fato. Os dois números saem de subconsultas
     * correlacionadas na mesma query da página, sobre os índices
     * `(tenant_id, fornecedor_id)` que as duas tabelas já têm.
     *
     * São o que a coluna "Compras" da listagem mostra, por `resumoDeCompras` — a
     * pergunta "este fornecedor está classificado?", respondida sem abrir o
     * cadastro. Zero e zero é resposta legítima: o fornecedor nascido do
     * orçamento não tem classificação nenhuma, e é isso que o resumo diz.
     */
    qtdGruposInsumos: z.number().int().nonnegative(),
    qtdInsumosNaoOfertados: z.number().int().nonnegative(),

    createdAt: dataHoraSchema,
    updatedAt: dataHoraSchema,
  })
  .merge(
    enderecoSchema.extend({
      /** Desnormalizado na leitura para a listagem não pedir a cidade à parte. */
      cidadeNome: z.string().nullable(),
      cidadeUf: ufSchema.nullable(),
    }),
  )
  .merge(contatoDoFornecedorRespostaSchema);

export type Fornecedor = z.infer<typeof fornecedorSchema>;

/**
 * O fornecedor com o que ele vende.
 *
 * Separado de `fornecedorSchema` pelo mesmo motivo do insumo detalhado: só a
 * leitura de **um** fornecedor carrega as listas. Devolvê-las na listagem
 * custaria duas consultas a mais por página, e listas sempre vazias na lista
 * seriam uma promessa que a tela leria como fato ("este fornecedor não atende
 * grupo nenhum").
 *
 * O que a listagem passou a mostrar são as **contagens**, em `fornecedorSchema`.
 * Elas dão a resposta que interessa ali — está classificado ou não — sem trazer
 * nome nenhum, e saem de subconsulta na própria query da página. Quem precisa
 * dos nomes abre o cadastro, que é onde eles são editáveis de qualquer forma.
 */
export const fornecedorDetalhadoSchema = fornecedorSchema.extend({
  gruposInsumos: z.array(fornecedorGrupoInsumoSchema),
  insumosNaoOfertados: z.array(fornecedorInsumoNaoOfertadoSchema),
});

export type FornecedorDetalhado = z.infer<typeof fornecedorDetalhadoSchema>;

export const listaFornecedoresSchema = respostaListaSchema(fornecedorSchema);
export type ListaFornecedores = z.infer<typeof listaFornecedoresSchema>;

export const listarFornecedoresQuerySchema = paginacaoSchema.extend({
  /** Ausente traz ativos e inativos; a listagem mostra ativos por padrão. */
  ativo: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  /**
   * Só quem tem **pedido de compra esperando entrega**.
   *
   * Existe para a entrada de nota por pedido (`CTL·03`): ali o fornecedor não é
   * um dado do cadastro, é a pergunta "de quem chegou material hoje". Com o
   * cadastro inteiro na lista, escolher qualquer um dos que nunca têm pedido
   * aberto devolvia a tabela vazia, e o usuário ficava sem saber se errou o
   * fornecedor, a empresa, ou se o pedido não existia.
   *
   * "Esperando entrega" é o mesmo recorte que a tela aplica na tabela: pedido
   * **pendente** com ao menos um item cujo saldo em aberto é maior que zero. A
   * segunda metade era feita no cliente, sobre a página, porque
   * `quantidade_em_aberto` é calculada na leitura — aqui ela vira `EXISTS`, que
   * é onde ela pode ser feita sobre o cadastro inteiro.
   *
   * **Não vira o padrão da listagem.** No cadastro de fornecedores a pergunta é
   * outra — quem existe —, e esconder quem não tem pedido aberto faria o
   * cadastro parecer menor do que é.
   */
  comPedidoPendente: booleanoDeQuerySchema,
  /**
   * Recorta o pedido pendente pela empresa que comprou.
   *
   * Sozinho não faz nada: só tem efeito com `comPedidoPendente`. É o par que a
   * entrada de nota usa — a nota é de uma empresa só, e um fornecedor com pedido
   * aberto em **outra** empresa do grupo levaria de volta à tabela vazia, que é
   * exatamente o que o filtro veio evitar.
   */
  empresaId: uuidSchema.optional(),
});

export type ListarFornecedoresQuery = z.infer<typeof listarFornecedoresQuerySchema>;

/**
 * `nomeDeUso` é a ordem **padrão**, e não `nome`.
 *
 * O fornecedor é exibido pelo nome fantasia, com a razão social de queda (ver
 * `nomeDoFornecedor`, na web, e `nomeDeUso`, na API). Ordenar por `nome` enquanto
 * a lista mostra o fantasia produz uma sequência que se lê como fora de ordem —
 * e o usuário conclui que a listagem está com defeito, não que há duas colunas.
 *
 * As duas colunas cruas continuam ordenáveis: quem confere cadastro às vezes
 * quer a razão social pura.
 */
export const COLUNAS_ORDENAVEIS_FORNECEDOR = [
  'nomeDeUso',
  'nome',
  'nomeFantasia',
  'createdAt',
] as const;
